import type { RouteRecordRaw } from 'vue-router'
import type { MenuPermission } from '@/api/menu-permission'

/**
 * 根据权限过滤和排序路由
 * @param routes 原始路由配置
 * @param permissions 用户权限列表
 * @returns 过滤后的路由配置
 */
export function filterRoutesByPermission(
  routes: RouteRecordRaw[],
  permissions: MenuPermission[]
): RouteRecordRaw[] {
  const permissionCodes = permissions
    .filter(p => p.status === 1) // 只取启用的权限
    .map(p => p.permissionCode)
  const itemMap = new Map(
    permissions
      .filter(p => p.status === 1)
      .map(p => [p.permissionCode, p])
  )

  const filteredRoutes = routes
    .map(route => processRoute(route, permissionCodes, itemMap))
    .filter(route => route !== null) as RouteRecordRaw[]

  applySettlementMenuOrder(filteredRoutes)

  return sortRoutes(filteredRoutes)
}

/**
 * 将菜单排在指定权限项之后（使用后端 sort + 0.5，与原型「仪表盘下方」一致）
 */
function applyAnchoredMenuOrder(
  route: RouteRecordRaw,
  itemMap: Map<string, MenuPermission>
): void {
  const anchorCode = route.meta?.insertAfterPermission as string | undefined
  if (!anchorCode || !route.meta || !itemMap.has(anchorCode)) {
    return
  }
  const anchor = itemMap.get(anchorCode)
  if (anchor && typeof anchor.sort === 'number') {
    route.meta.order = anchor.sort + 0.5
  }
}

/**
 * 处理单个路由（递归处理子路由）
 * @param route 路由配置
 * @param permissionCodes 权限代码列表
 * @param itemMap 详情映射
 * @returns 处理后的路由或null
 */
function processRoute(
  route: RouteRecordRaw,
  permissionCodes: string[],
  itemMap: Map<string, any>
): RouteRecordRaw | null {
  // 浅拷贝路由配置，保留component函数，深拷贝meta对象
  const clonedRoute: RouteRecordRaw = {
    ...route,
    meta: route.meta ? { ...route.meta } : undefined
  }

  // 将后端权限配置中的 sort / title 映射到路由 meta（同 permission 多路由时需 preserveRouteMeta）
  if (clonedRoute.meta?.permission && itemMap.has(clonedRoute.meta.permission as string)) {
    const item = itemMap.get(clonedRoute.meta.permission as string)
    const preserveRouteMeta = clonedRoute.meta.preserveRouteMeta === true
    if (!preserveRouteMeta && item && typeof item.sort === 'number') {
      clonedRoute.meta.order = item.sort
    }
    if (!preserveRouteMeta && item && item.name && item.nameEn) {
      // 同时保留中英文标题，供菜单/页签/面包屑在运行时按当前语言切换，避免路由注册时固化语言。
      clonedRoute.meta.title = item.name
      clonedRoute.meta.titleEn = item.nameEn
    }
  }

  // 检查当前路由权限
  // 如果路由没有permission字段，默认显示（基础路由如登录页等
  const hasCurrentPermission = !clonedRoute.meta?.permission ||
    permissionCodes.includes(clonedRoute.meta.permission as string)

  // 处理子路由
  if (clonedRoute.children && clonedRoute.children.length > 0) {
    const filteredChildren = clonedRoute.children
      .map(child => processRoute(child, permissionCodes, itemMap))
      .filter(child => child !== null) as RouteRecordRaw[]

    // 对子路由进行排序
    clonedRoute.children = sortRoutes(filteredChildren)

    // 父子权限继承检查：如果父级无权限但子级有权限，隐藏整个分支
    if (!hasCurrentPermission && filteredChildren.length > 0) {
      console.warn(
        `父级路由 ${String(clonedRoute.name) || clonedRoute.path} 无权限，但子级有权限，将隐藏整个分支`,
        {
          parentRoute: String(clonedRoute.name) || clonedRoute.path,
          parentPermission: clonedRoute.meta?.permission,
          childCount: filteredChildren.length
        }
      )
      return null
    }

    // 如果是菜单组且所有子路由都被过滤了，隐藏父级
    if (filteredChildren.length === 0 && clonedRoute.meta?.isMenuGroup) {
      return null
    }
  }

  // 如果当前路由没有权限，返回null
  if (!hasCurrentPermission) {
    return null
  }

  applyAnchoredMenuOrder(clonedRoute, itemMap)

  return clonedRoute
}

/**
 * 根据权限sort字段排序路由
 * @param routes 路由列表
 * @param sortMap 排序映射
 * @returns 排序后的路由列表
 */
function sortRoutes(routes: RouteRecordRaw[]): RouteRecordRaw[] {
  return routes.sort((a, b) => {
    const sortA = getRouteSort(a)
    const sortB = getRouteSort(b)
    return sortA - sortB
  })
}

/**
 * 获取路由的排序值
 * @param route 路由配置
 * @returns 排序值
 */
function getRouteSort(route: RouteRecordRaw): number {
  // 使用路由meta中的order（已经通过processRoute映射了后端sort值）
  if (route.meta?.order) {
    return route.meta.order as number
  }

  // 默认排序值
  return 9999
}

const ORDER_CENTER_PERMISSION = 'module:order-center:order-center'
const SHIPPING_CENTER_PERMISSION = 'module:schedule-center:schedule-center'

/** 结算菜单无后端 sort 时，插在订单中心与船期中心之间 */
function applySettlementMenuOrder(routes: RouteRecordRaw[]): void {
  for (const route of routes) {
    if (route.name !== 'Layout' || !route.children?.length) continue

    const children = route.children
    const settlement = children.find(r => r.name === 'Settlement')
    const orderCenter = children.find(r => r.meta?.permission === ORDER_CENTER_PERMISSION)
    const shipping = children.find(r => r.meta?.permission === SHIPPING_CENTER_PERMISSION)

    if (!settlement?.meta || !orderCenter || !shipping) continue

    const orderCenterOrder = getRouteSort(orderCenter)
    const shippingOrder = getRouteSort(shipping)
    if (orderCenterOrder >= shippingOrder) continue

    settlement.meta.order = orderCenterOrder + (shippingOrder - orderCenterOrder) / 2
    route.children = sortRoutes(children)
  }
}

/**
 * 检查用户是否有特定权限
 * @param permissionCode 权限代码
 * @param permissions 用户权限列表
 * @returns 是否有权限
 */
export function hasPermission(permissionCode: string, permissions: MenuPermission[]): boolean {
  return permissions.some(p =>
    p.permissionCode === permissionCode && p.status === 1
  )
}

/**
 * 检查用户是否有任意一个权限
 * @param permissionCodes 权限代码列表
 * @param permissions 用户权限列表
 * @returns 是否有任意权限
 */
export function hasAnyPermission(permissionCodes: string[], permissions: MenuPermission[]): boolean {
  return permissionCodes.some(code => hasPermission(code, permissions))
}

/**
 * 检查用户是否有所有权限
 * @param permissionCodes 权限代码列表
 * @param permissions 用户权限列表
 * @returns 是否有所有权限
 */
export function hasAllPermissions(permissionCodes: string[], permissions: MenuPermission[]): boolean {
  return permissionCodes.every(code => hasPermission(code, permissions))
}

/**
 * 获取用户拥有的权限代码列表
 * @param permissions 用户权限列表
 * @returns 权限代码列表
 */
export function getPermissionCodes(permissions: MenuPermission[]): string[] {
  return permissions
    .filter(p => p.status === 1)
    .map(p => p.permissionCode)
}

/**
 * 调试工具：打印路由过滤结果
 * @param originalRoutes 原始路由
 * @param filteredRoutes 过滤后路由
 * @param permissions 权限列表
 */
export function debugRouteFilter(
  originalRoutes: RouteRecordRaw[],
  filteredRoutes: RouteRecordRaw[],
  permissions: MenuPermission[]
): void {
  if (import.meta.env.MODE === 'development') {
    console.group('🔍 路由权限过滤调试信息')
    console.log('原始路由数量:', countRoutes(originalRoutes))
    console.log('过滤后路由数量:', countRoutes(filteredRoutes))
    console.log('用户权限列表:', permissions.filter(p => p.status === 1).map(p => p.permissionCode))
    console.log('过滤后的路由结构:', filteredRoutes)
    console.groupEnd()
  }
}

/**
 * 统计路由数量（包含子路由）
 * @param routes 路由列表
 * @returns 路由总数
 */
function countRoutes(routes: RouteRecordRaw[]): number {
  let count = routes.length
  routes.forEach(route => {
    if (route.children) {
      count += countRoutes(route.children)
    }
  })
  return count
}
